import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useUI } from "@/context/UIContext";

export default function Header() {
  const [scrolled, setScrolled] = useState(() => typeof window !== "undefined" && window.scrollY > 8);
  const [hiddenForHero, setHiddenForHero] = useState(() => {
    if (typeof window === "undefined") return false;
    const isHomeInit = window.location.pathname === "/";
    if (!isHomeInit) return false;
    return window.scrollY < window.innerHeight * 2 - 40;
  });
  const [open, setOpen] = useState(false);
  const { openCart, openSearch, count } = useUI();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8);
      if (isHome) {
        // Hero scroll container is 300vh with a 100vh sticky stage;
        // reveal the header once the sticky stage is about to release.
        const heroEnd = window.innerHeight * 2;
        setHiddenForHero(window.scrollY < heroEnd - 40);
      } else {
        setHiddenForHero(false);
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isHome]);

  const nav = [
    { to: "/shop", label: "Shop" },
    { to: "/collections", label: "Collections" },
    { to: "/about", label: "About" },
  ];

  // Nav typography: 14px, weight 400, uppercase, 0.16em tracking
  const linkClass = "nav-link text-[14px] font-normal tracking-[0.16em] uppercase text-black whitespace-nowrap";
  const iconBtn = "w-10 h-10 flex items-center justify-center cursor-pointer";

  return (
    <header
      className={`fixed top-[32px] left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-canvas border-b border-stone" : "bg-transparent"
      } ${hiddenForHero ? "opacity-0 -translate-y-6 pointer-events-none" : "opacity-100 translate-y-0"}`}
    >
      <div className="shell h-[56px] md:h-[84px] grid grid-cols-[1fr_auto_1fr] items-center">
        {/* LEFT */}
        <div className="flex items-center gap-10">
          {/* mobile menu button */}
          <button
            className={`md:hidden ${iconBtn} -ml-2`}
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            <i className="ri-menu-line text-[18px]"></i>
          </button>
          <nav className="hidden md:flex items-center gap-10">
            {nav.map((n) => (
              <NavLink key={n.label} to={n.to} className={({ isActive }) => `${linkClass} ${isActive ? "active" : ""}`}>{n.label}</NavLink>
            ))}
          </nav>
        </div>

        {/* CENTER — geometrically centered via grid */}
        <Link
          to="/"
          className="font-display text-[20px] md:text-[26px] tracking-[0.22em] md:tracking-[0.24em] text-brass whitespace-nowrap justify-self-center transition-opacity duration-200 hover:opacity-75"
        >
          AURELIN
        </Link>

        {/* RIGHT */}
        <div className="flex items-center gap-6 justify-self-end">
          <button
            onClick={openSearch}
            className={`hidden md:inline-flex ${linkClass} cursor-pointer items-center`}
            aria-label="Search"
          >
            Search
          </button>
          <button
            onClick={openSearch}
            className={`md:hidden ${iconBtn}`}
            aria-label="Search"
          >
            <i className="ri-search-line text-[16px]"></i>
          </button>
          <button
            onClick={openCart}
            className={`relative ${iconBtn} -mr-2`}
            aria-label="Bag"
          >
            <i className="ri-shopping-bag-line text-[16px]"></i>
            {count > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-ink text-canvas text-[10px] flex items-center justify-center">{count}</span>
            )}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-canvas border-t border-stone">
          <div className="shell py-6 flex flex-col gap-4">
            <Link to="/shop" onClick={() => setOpen(false)} className={linkClass}>Shop</Link>
            <Link to="/collections" onClick={() => setOpen(false)} className={linkClass}>Collections</Link>
            <Link to="/about" onClick={() => setOpen(false)} className={linkClass}>About</Link>
            <button onClick={() => { setOpen(false); openSearch(); }} className={`${linkClass} text-left cursor-pointer`}>Search</button>
          </div>
        </div>
      )}
    </header>
  );
}